import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBranchesThunk } from "../../../store/thunks/branchThunks";
import { ChartBarIcon, CheckIcon, XIcon } from "@heroicons/react/outline";
import { CITY_TIERS } from "constants/branch.constants";

const BranchComparison = () => {
  const dispatch = useDispatch();
  const { branches, loadingList } = useSelector((state) => state.branch);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);

  useEffect(() => {
    dispatch(getBranchesThunk({ isActive: true }));
  }, [dispatch]);

  useEffect(() => {
    if (selectedBranches.length > 0) {
      const data = branches.filter((b) => selectedBranches.includes(b._id));
      setComparisonData(data);
    } else {
      setComparisonData([]);
    }
  }, [selectedBranches, branches]);

  const toggleBranch = (branchId) => {
    if (selectedBranches.includes(branchId)) {
      setSelectedBranches((prev) => prev.filter((id) => id !== branchId));
    } else if (selectedBranches.length < 4) {
      setSelectedBranches((prev) => [...prev, branchId]);
    }
  };

  const getMetricValue = (branch, metric) => {
    switch (metric) {
      case "type":
        return branch.branchType;
      case "city":
        return branch.address?.city;
      case "state":
        return branch.address?.state;
      case "stores":
        return branch.storeCount || 0;
      case "rateMode":
        return branch.rateConfig?.rateMode || "ADMIN";
      case "ratePolicy":
        return branch.rateConfig?.ratePolicy || "INHERIT";
      case "highValueThreshold":
        return `₹${(
          branch.settings?.highValueThreshold || 50000
        ).toLocaleString()}`;
      case "maxDiscount":
        return `${branch.settings?.maxDiscountPercent || 10}%`;
      case "autoSync":
        return branch.rateConfig?.autoSync?.enabled ? "Yes" : "No";
      case "isMainBranch":
        return branch.isMainBranch ? "Yes" : "No";
      case "cityTier":
        return CITY_TIERS[branch.cityTier] || "-";
      case "cashLimit":
        return `₹${(branch.financial?.cashLimit || 0).toLocaleString()}`;
      case "establishmentYear":
        return branch.compliance?.establishmentYear || "-";
      default:
        return "-";
    }
  };

  const metrics = [
    { key: "type", label: "Branch Type", category: "Basic" },
    { key: "city", label: "City", category: "Location" },
    { key: "state", label: "State", category: "Location" },
    { key: "stores", label: "Store Count", category: "Operations" },
    { key: "rateMode", label: "Rate Mode", category: "Configuration" },
    { key: "ratePolicy", label: "Rate Policy", category: "Configuration" },
    {
      key: "highValueThreshold",
      label: "High Value Threshold",
      category: "Settings",
    },
    { key: "maxDiscount", label: "Max Discount", category: "Settings" },
    { key: "autoSync", label: "Auto Sync", category: "Configuration" },
    { key: "isMainBranch", label: "Main Branch", category: "Basic" },
    { key: "cityTier", label: "City Tier", category: "Location" },
    { key: "parentBranch", label: "Parent Branch", category: "Hierarchy" },
    { key: "cashLimit", label: "Cash Limit", category: "Financial" },
    {
      key: "establishmentYear",
      label: "Establishment Year",
      category: "Compliance",
    },
    {
      key: "crossBranchRedemption",
      label: "Cross-Branch Redemption",
      category: "Settings",
    },
    {
      key: "interBranchTransfer",
      label: "Inter-Branch Transfer",
      category: "Settings",
    },
  ];

  const categories = [...new Set(metrics.map((m) => m.category))];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center">
            <ChartBarIcon className="h-6 w-6 mr-2" />
            Branch Comparison
          </h2>
          <span className="text-sm text-gray-500">
            Select up to 4 branches to compare
          </span>
        </div>

        {/* Branch Selection */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
          {loadingList ? (
            <div className="col-span-full flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            branches.map((branch) => (
              <button
                key={branch._id}
                onClick={() => toggleBranch(branch._id)}
                disabled={
                  !selectedBranches.includes(branch._id) &&
                  selectedBranches.length >= 4
                }
                className={`p-3 border-2 rounded-lg text-left transition-all ${
                  selectedBranches.includes(branch._id)
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                }`}
              >
                <div className="font-semibold text-sm">{branch.name}</div>
                <div className="text-xs text-gray-500">{branch.code}</div>
              </button>
            ))
          )}
        </div>

        {/* Comparison Table */}
        {comparisonData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-semibold">Metric</th>
                  {comparisonData.map((branch) => (
                    <th key={branch._id} className="text-left py-2 px-3">
                      <div>
                        <div className="font-semibold">{branch.name}</div>
                        <div className="text-xs text-gray-500">
                          {branch.code}
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => (
                  <React.Fragment key={category}>
                    <tr>
                      <td
                        colSpan={comparisonData.length + 1}
                        className="py-2 px-3 bg-gray-50 font-semibold text-sm"
                      >
                        {category}
                      </td>
                    </tr>
                    {metrics
                      .filter((m) => m.category === category)
                      .map((metric) => (
                        <tr key={metric.key} className="border-b">
                          <td className="py-2 px-3 text-sm font-medium text-gray-700">
                            {metric.label}
                          </td>
                          {comparisonData.map((branch) => {
                            const value = getMetricValue(branch, metric.key);
                            const isBoolean = value === "Yes" || value === "No";

                            return (
                              <td key={branch._id} className="py-2 px-3">
                                {isBoolean ? (
                                  value === "Yes" ? (
                                    <CheckIcon className="h-5 w-5 text-green-600" />
                                  ) : (
                                    <XIcon className="h-5 w-5 text-gray-400" />
                                  )
                                ) : (
                                  <span className="text-sm">{value}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            Select branches to compare
          </div>
        )}
      </div>
    </div>
  );
};

export default BranchComparison;
